import { Button } from "@/components/ui/button";
import { Link, Navigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { getPostAuthRoute } from "@/utils/routePermissions";
export default function Landing() {
  const { userId, role, loading } = useUser();

  // An already-signed-in user has no reason to read the marketing page; send
  // them where /auth would have sent them. Only `userId` gates the redirect,
  // but the destination depends on `role`, so wait for the profile to resolve
  // before choosing. Anonymous visitors never hit this branch and so never see
  // a spinner in place of the page.
  if (userId) {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      );
    }
    return <Navigate to={getPostAuthRoute([role])} replace />;
  }

  const features = ["Track employee performance and costs", "Monitor project budgets and timelines", "Generate financial reports and insights", "Real-time profit and loss tracking", "Team collaboration tools", "Custom dashboards and analytics"];
  const pricingPlans = [{
    name: "Starter",
    price: "$49",
    features: ["Up to 10 employees", "Basic reporting", "Email support"]
  }, {
    name: "Professional",
    price: "$99",
    features: ["Up to 50 employees", "Advanced analytics", "Priority support"]
  }, {
    name: "Enterprise",
    price: "Custom",
    features: ["Unlimited employees", "Custom features", "24/7 support"]
  }];
  return <div className="min-h-screen">
      {/* Hero Section */}
      <div className="container mx-auto py-20 text-center">
        <h1 className="text-5xl font-bold mb-6">
          Take Control of Your Company's Finances
        </h1>
        <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
          Overseer helps you track employee costs, project earnings, and overall
          company finances in one powerful platform.
        </p>
        <div className="flex gap-4 justify-center">
          <Link to="/auth">
            <Button size="lg">Get Started</Button>
          </Link>
          <Button variant="outline" size="lg">
            Learn More
          </Button>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-muted py-20">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Everything You Need to Manage Your Finances
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => <div key={index} className="flex items-start gap-4 p-6 bg-background rounded-lg shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0" />
                <p className="text-lg">{feature}</p>
              </div>)}
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="container mx-auto py-20">
        <h2 className="text-3xl font-bold text-center mb-12">Pricing</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {pricingPlans.map((plan, index) => <div key={index} className="border rounded-lg p-8 text-center hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
              <p className="text-3xl font-bold mb-6">
                {plan.price}
                {plan.price !== "Custom" && <span className="text-sm">/month</span>}
              </p>
              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, featureIndex) => <li key={featureIndex} className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    <span>{feature}</span>
                  </li>)}
              </ul>
              <Link to="/auth">
                <Button className="w-full">Get Started</Button>
              </Link>
            </div>)}
        </div>
      </div>
    </div>;
}